import React, { useEffect, useState } from "react";

import { Button, CircularProgress, Grid, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import {
  ArrayInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
} from "react-admin";
import { useField, useForm } from "react-final-form";

import {
  CARRIER_ID_SCHEME_CHOICES,
  CARRIER_ID_SCHEMES,
  carrierIdSchemeLabel,
  normalizeCarrierIdentifierValue,
  validateCarrierIdentifier,
} from "../../utils/carrierIdentity";
import {
  describeLookup,
  isFmcsaScheme,
  lookupCarrierIdentifier,
} from "../../utils/carrierLookup";

/**
 * The carrier's regulatory numbers, as repeatable scheme + value rows.
 *
 * Shared by every form that captures them — the manager's Carriers create and
 * edit forms, the inline create dialog, and the carrier's own company profile —
 * so all four normalise and check a typed number the same way. The rules live in
 * utils/carrierIdentity; this is only their form.
 *
 * A row is (scheme, value) rather than a column per authority because no single
 * identifier is global: a carrier running into the US from Mexico holds a USDOT
 * number and an RFC, and a fourth country is then one more row rather than a
 * schema change.
 *
 * A USDOT or docket number is also checked against FMCSA's register as it is
 * typed, and the registered legal name offered for the business name. That is the
 * cheapest place to catch a wrong number: a mistyped USDOT is the main way one
 * carrier ends up as two companies, and here somebody can still ask the driver.
 */

const validateRequired = required();

// Both halves are required once a row exists: a scheme with no number identifies
// nothing, and a number with no scheme cannot be compared to anything, since
// matching is per scheme.
const validateScheme = [validateRequired];

/**
 * The schemes this row may pick: every one the table knows, less the ones the
 * other rows already hold.
 *
 * normalizeCarrierIdentifiers keeps one row per scheme, so a second USDOT row
 * would be dropped on save with nothing said and the number written nowhere.
 * Taking the choice away is what makes that unreachable — the row's own scheme
 * stays listed, or picking it again would blank the field the user is looking at.
 *
 * Keyed on the row's own scheme rather than on its position, so the row does not
 * have to know its index: it is the value that has to stay listed, and one scheme
 * is held by at most one row once the rows are normalised.
 */
const schemeChoices = (identifiers, ownScheme) => {
  const taken = new Set(
    (Array.isArray(identifiers) ? identifiers : [])
      .map((row) => row?.scheme)
      .filter((scheme) => scheme && scheme !== ownScheme),
  );
  return CARRIER_ID_SCHEME_CHOICES.filter((choice) => !taken.has(choice.id));
};

// The row index comes from the field's own path ("identifiers[1].value") and the
// scheme from allValues, so this stays one module-level function. Building it per
// render instead would hand react-admin a new validator on every keystroke,
// which re-registers the field mid-edit.
const validateIdentifierValue = (value, allValues, meta) => {
  const index = Number(String(meta?.name ?? "").match(/\[(\d+)\]/)?.[1] ?? -1);
  const scheme = allValues?.identifiers?.[index]?.scheme;
  return validateCarrierIdentifier({ scheme, value }) || undefined;
};

const validateValue = [validateRequired, validateIdentifierValue];

// A non-empty space, so the field does not resize when a scheme is picked.
const SCHEME_PLACEHOLDER_HINT = " ";

const schemeHint = (scheme) =>
  CARRIER_ID_SCHEMES.find((entry) => entry.id === scheme)?.hint ??
  SCHEME_PLACEHOLDER_HINT;

/**
 * What to tell the user under the Number field: normally the scheme's format, but
 * the number as it will actually be stored once it differs from what was typed.
 *
 * Shown rather than written into the field. Rewriting the value as the user types
 * fights a controlled input — the separators they are mid-way through typing get
 * eaten and the caret jumps — and rewriting it on blur only moves the same fight
 * to the moment they tab away. Nothing is lost by leaving it: every save path
 * normalises through normalizeCarrierIdentifiers, so the stored number is the
 * reduced one whatever the field held.
 */
const valueHelperText = (scheme, value) => {
  if (!scheme) return SCHEME_PLACEHOLDER_HINT;
  const normalized = normalizeCarrierIdentifierValue(scheme, value);
  if (normalized && value && normalized !== value)
    return `Stored as ${normalized}`;
  return schemeHint(scheme);
};

// Long enough that a number is not looked up letter by letter, short enough that
// the answer is there by the time the user has read what they typed. Measured
// from the last keystroke, so a correction restarts it.
const LOOKUP_DEBOUNCE_MS = 700;

/**
 * Ask FMCSA about a number once the user has stopped typing it.
 *
 * Only for a number that already passes its scheme's own format check — a partial
 * USDOT number is a number nobody holds, and reporting that while somebody is
 * still typing would flag every carrier in the book as unregistered on the way in.
 *
 * @returns {{checking: boolean, result: object|null}}
 */
const useCarrierLookup = (scheme, value) => {
  const [state, setState] = useState({ checking: false, result: null });

  useEffect(() => {
    if (
      !isFmcsaScheme(scheme) ||
      validateCarrierIdentifier({ scheme, value })
    ) {
      setState({ checking: false, result: null });
      return undefined;
    }

    // Guards the state write rather than the request: the answer for a number
    // the user has already moved past would otherwise land on the row and stay
    // there. The lookup itself is cached, so letting it finish costs nothing.
    let live = true;
    setState({ checking: true, result: null });

    const timer = setTimeout(async () => {
      const result = await lookupCarrierIdentifier({ scheme, value });
      if (live) setState({ checking: false, result });
    }, LOOKUP_DEBOUNCE_MS);

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [scheme, value]);

  return state;
};

/**
 * What the register said about this row, and the offer to take the name from it.
 *
 * The name is offered rather than applied. FMCSA holds the registered legal
 * entity, which is regularly not what the yard's paperwork, the driver's door or
 * the bill of lading says — "HAUL OF FAME LLC" against "Haul of Fame". Overwriting
 * what somebody typed would be wrong as often as it was right; showing both and
 * letting them choose is not.
 */
const LookupVerdict = ({ scheme, value }) => {
  const form = useForm();
  const { checking, result } = useCarrierLookup(scheme, value);
  const verdict = describeLookup(result);

  if (checking)
    return (
      <Typography variant="caption" color="textSecondary">
        <CircularProgress size={10} thickness={6} /> Checking FMCSA…
      </Typography>
    );

  if (!verdict) return null;

  const { legalName } = verdict;
  // Offered only when it would actually change something, so an accepted name
  // does not keep asking to be accepted.
  const businessName = form.getState().values?.businessName;
  const offerName = legalName && legalName !== businessName;

  return (
    <Alert severity={verdict.severity} icon={false}>
      <Typography variant="caption" component="span">
        {verdict.text}
      </Typography>
      {offerName && (
        <Button
          size="small"
          color="primary"
          onClick={() => form.change("businessName", legalName)}
        >
          Use this name
        </Button>
      )}
    </Alert>
  );
};

/**
 * One row of the iterator. `source` is the row's path in the form, which is what
 * both the helper text and the validator key on.
 *
 * Subscribed field by field rather than through the whole form's values: this row
 * needs its own two values and the schemes its siblings hold, and nothing else. A
 * values subscription re-rendered every row on every keystroke anywhere in the
 * form — the business name and the phone number included — and made the row find
 * itself by parsing its index back out of `source`.
 */
const IdentifierRow = ({ source, disabled }) => {
  const {
    input: { value: identifiers },
  } = useField("identifiers", { subscription: { value: true } });
  const {
    input: { value: scheme },
  } = useField(`${source}.scheme`, { subscription: { value: true } });
  const {
    input: { value },
  } = useField(`${source}.value`, { subscription: { value: true } });

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={5}>
        <SelectInput
          source={`${source}.scheme`}
          label="Issued by"
          variant="outlined"
          fullWidth
          margin="dense"
          disabled={disabled}
          choices={schemeChoices(identifiers, scheme)}
          validate={validateScheme}
        />
      </Grid>
      <Grid item xs={12} sm={7}>
        <TextInput
          source={`${source}.value`}
          label="Number"
          variant="outlined"
          fullWidth
          margin="dense"
          disabled={disabled}
          helperText={valueHelperText(scheme, value)}
          validate={validateValue}
        />
      </Grid>
      {/* Not while the field is read-only: the numbers on a carrier's own record
          are checked where they are entered, and a yard looking at a profile it
          does not own has nothing to do with the answer. */}
      {!disabled && (
        <Grid item xs={12}>
          <LookupVerdict scheme={scheme} value={value} />
        </Grid>
      )}
    </Grid>
  );
};

const DEFAULT_HELPER_TEXT =
  "Optional. A USDOT number identifies a carrier across all three countries — FMCSA issues one to every carrier operating in the US, Canadian and Mexican carriers included. USDOT, MC and MX numbers are checked against FMCSA's register as you type them.";

/**
 * @param {object} props
 * @param {boolean} [props.disabled] - show the numbers without letting them be
 *   changed; the manager's form uses this once the carrier owns its own profile
 * @param {string} [props.helperText] - replaces the default note, so a disabled
 *   field can say why
 */
export const CarrierIdentifiersInput = ({ disabled, helperText }) => (
  <>
    <Typography variant="subtitle2" color="textSecondary">
      ID Numbers
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {helperText ?? DEFAULT_HELPER_TEXT}
    </Typography>
    <ArrayInput source="identifiers" label="">
      <SimpleFormIterator disableAdd={disabled} disableRemove={disabled}>
        <IdentifierRow disabled={disabled} />
      </SimpleFormIterator>
    </ArrayInput>
  </>
);

/**
 * The same numbers read-only, for a screen that shows them without offering to
 * change them.
 */
export const CarrierIdentifiersField = ({ record }) => {
  const identifiers = Array.isArray(record?.identifiers)
    ? record.identifiers
    : [];

  if (identifiers.length === 0)
    return (
      <Typography variant="body2" color="textSecondary">
        No ID numbers on record.
      </Typography>
    );

  return (
    <Grid container spacing={1}>
      {/* Keyed by position, not by contents: a record written before the
          per-scheme dedup can hold two identical rows, which would collide. */}
      {identifiers.map((identifier, index) => (
        <Grid item xs={12} key={index}>
          <Typography variant="body2">
            {`${carrierIdSchemeLabel(identifier?.scheme)}: ${identifier?.value}`}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
};
